import { useContext, useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { GlobalContext } from "../../../../Context/GlobalContext";

/** tiny format helpers kept local */
const fmt = {
  dash: (v) => (v === null || v === undefined || v === "" ? "—" : v),
};

export default function useOrderSettings() {
  const { api, api2, baseUrl, adminId, latitude, longitude } =
    useContext(GlobalContext);

  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const setSpin = (id, action) => setBusy((p) => ({ ...p, [id]: action }));
  const clearSpin = (id) => setBusy((p) => ({ ...p, [id]: null }));

  const getCatalogBannersAdmin = async (userid) => {
    try {
      const { data } = await api.post("/Order/v1.0/getCatalogBannersAdmin", {
        userid,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data?.response || [],
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed to fetch banners",
          data: [],
        };
      }
    } catch (e) {
      console.error("getCatalogBannersAdmin error:", e);
      return {
        success: false,
        message: "Error fetching banners",
        data: [],
      };
    }
  };
  const addCatalogBanner = async ({ userid, form }) => {
    try {
      const { data } = await api.post("/Order/v1.0/addCatalogBanners", {
        ...form,
        userid,
        bannerid: "",
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Banner created",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("addCatalogBanners error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const deleteCatalogBanner = async (bannerid) => {
    try {
      const { data } = await api.post("/Order/v1.0/deleteCatalogBanners", {
        bannerid,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Deleted",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("deleteCatalogBanners error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const getDeliveryProvider = async (userid) => {
    try {
      const { data } = await api.post("/Order/getDeliveryProvider", {
        userid,
        secret: "string",
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          deliveryPartner: data?.response?.deliveryPartner || "",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed to fetch delivery provider",
          deliveryPartner: "",
        };
      }
    } catch (e) {
      console.error("getDeliveryProvider error:", e);
      return {
        success: false,
        message: "Error fetching delivery provider",
        deliveryPartner: "",
      };
    }
  };
  const updateDeliveryProvider = async ({ userid, deliveryPartner }) => {
    try {
      const { data } = await api.post("/Order/updateDeliveryProvider", {
        userid,
        deliveryPartner,
        secret: "string",
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Delivery partner updated",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed to update",
        };
      }
    } catch (e) {
      console.error("updateDeliveryProvider error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const getSettlementAccountList = async (userid) => {
    try {
      const { data } = await api.post(
        "/Transactions/getSettlementAccountList",
        { userid },
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data?.response || [],
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed to fetch bank accounts",
          data: [],
        };
      }
    } catch (e) {
      console.error("getSettlementAccountList error:", e);
      return {
        success: false,
        message: "Error fetching bank accounts",
        data: [],
      };
    }
  };
  const addSettlementAccount = async ({
    userid,
    account,
    ifsc,
    bankName,
    bankHolderName,
  }) => {
    try {
      const { data } = await api.post("/Transactions/addSettlementAccount", {
        userid,
        account,
        ifsc,
        bankName,
        bankHolderName,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Bank added",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("addSettlementAccount error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const getOrganizationDetails = async (userid) => {
    try {
      const selectedUserId = String(userid || "").trim();

      if (!selectedUserId) {
        return {
          success: false,
          message: "Client user ID not found",
          data: {},
        };
      }

      const { data } = await api.post(
        "/User/getOrganizationDetails",
        { userid: selectedUserId },
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        },
      );

      if (data?.responseCode === "00") {
        const responseData = Array.isArray(data?.response)
          ? data.response[0] || {}
          : data?.response || {};

        return {
          success: true,
          data: responseData,
        };
      }

      return {
        success: false,
        message:
          data?.responseMessage || "Failed to fetch organization details",
        data: {},
      };
    } catch (e) {
      console.error("getOrganizationDetails error:", e);
      return {
        success: false,
        message:
          e?.response?.data?.responseMessage ||
          "Error fetching organization details",
        data: {},
      };
    }
  };
  const editOrganizationInfo = async ({
    userid,
    organizationName = "",
    gst = "",
    pan_no = "",
    brand_image = "",
    pament_gateway = "",
    opening_time = "",
    closing_time = "",
  }) => {
    try {
      const payload = {
        userid: String(userid || "").trim(),
        organizationName: String(organizationName || "").trim(),
        gst: String(gst || "").trim(),
        pan_no: String(pan_no || "").trim(),
        brand_image: String(brand_image || "").trim(),
        pament_gateway: String(pament_gateway || "").trim(),
        opening_time: String(opening_time || "").trim(),
        closing_time: String(closing_time || "").trim(),
      };

      if (!payload.userid) {
        return {
          success: false,
          message: "Client user ID not found",
        };
      }

      const { data } = await api.post("/User/editOrganizationInfo", payload, {
        headers: {
          accept: "*/*",
          "Content-Type": "application/json-patch+json",
        },
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message:
            data?.responseMessage ||
            data?.response?.message ||
            "Organization updated",
          data: data?.response || {},
        };
      }

      return {
        success: false,
        message: data?.responseMessage || "Failed to update organization",
        data: data?.response || {},
      };
    } catch (e) {
      console.error("editOrganizationInfo error:", e);
      return {
        success: false,
        message:
          e?.response?.data?.responseMessage || "Failed to update organization",
        data: {},
      };
    }
  };
  const getMerchantCouponCodes = async (userid) => {
    try {
      const { data } = await api.post("/Order/v1.0/getMerchantCouponCodes", {
        userid,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data?.data || [],
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed to fetch coupon codes",
          data: [],
        };
      }
    } catch (e) {
      console.error("getMerchantCouponCodes error:", e);
      return {
        success: false,
        message: "Error fetching coupon codes",
        data: [],
      };
    }
  };
  const createMerchantCouponCode = async ({
    userid,
    couponCode,
    discount,
    discountType,
    new_user,
    singleUse,
    minOrderAmount,
    maxDiscountAmount,
    couponTitle,
    couponDiscription,
  }) => {
    try {
      const { data } = await api.post("/Order/v1.0/createMerchantCouponCode", {
        userid,
        couponCode,
        discount,
        discountType,
        new_user,
        singleUse,
        minOrderAmount,
        maxDiscountAmount,
        couponTitle,
        couponDiscription,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Coupon created",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("createMerchantCouponCode error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const disableMerchantCouponCode = async ({
    userid,
    couponId,
    operation = "DELETE",
  }) => {
    try {
      const { data } = await api.post("/Order/v1.0/disableMerchantCouponCode", {
        userid,
        couponId,
        operation,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Deleted",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("disableMerchantCouponCode error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const getDeliveryChargesAndDiscount = async (userid) => {
    try {
      const selectedUserId = String(userid || "").trim();

      if (!selectedUserId) {
        return {
          success: false,
          message: "Client user ID not found",
          discount: [],
          charges: [],
        };
      }

      const { data } = await api.post(
        "/Order/v1.0/getDeliveryChargesAndDiscount",
        {
          userid: selectedUserId,
        },
      );

      console.log("DELIVERY CHARGES AND DISCOUNT RESPONSE:", data);

      if (data?.responseCode === "00") {
        const responseData = data?.data || {};

        const discount = Array.isArray(responseData?.discount)
          ? responseData.discount
          : [];

        const charges = Array.isArray(responseData?.charges)
          ? responseData.charges
          : [];

        return {
          success: true,
          message: data?.responseMessage || "Fetched successfully",

          discount,
          charges,

          // Optional: full response data
          data: responseData,
        };
      }

      return {
        success: false,
        message:
          data?.responseMessage ||
          "Failed to fetch delivery charges and discounts",
        discount: [],
        charges: [],
        data: {},
      };
    } catch (error) {
      console.error("getDeliveryChargesAndDiscount error:", error);

      return {
        success: false,
        message:
          error?.response?.data?.responseMessage ||
          "Failed to fetch delivery charges and discounts",
        discount: [],
        charges: [],
        data: {},
      };
    }
  };
  const updateDeliveryCharges = async ({
    userid,
    charges,
    fromAmount,
    toAmount,
    chargeType,
  }) => {
    try {
      const { data } = await api.post("/Order/v1.0/updateDeliveryCharges", {
        userid,
        charges,
        fromAmount,
        toAmount,
        chargeType,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Delivery charges updated",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("updateDeliveryCharges error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const deleteDeliveryCharges = async ({ userid, chargeId }) => {
    try {
      const { data } = await api.post("/Order/v1.0/deleteDeliveryCharges", {
        userid,
        chargeId,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Removed",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("deleteDeliveryCharges error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const updateDiscount = async ({
    userid,
    discount,
    fromAmount,
    toAmount,
    discountType,
    orderType,
    maxDiscount,
  }) => {
    try {
      const { data } = await api.post("/Order/v1.0/updateDiscount", {
        userid,
        discount,
        fromAmount,
        toAmount,
        discountType,
        orderType,
        maxDiscount,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Discount updated",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("updateDiscount error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const deleteDiscount = async ({ userid, discountId }) => {
    try {
      const { data } = await api.post("/Order/v1.0/deleteDiscount", {
        userid,
        discountId,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Removed",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("deleteDiscount error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const getMerchantMenuItems = async (userid) => {
    try {
      const { data } = await api.post("/Order/v1.0/getMerchantMenuItems", {
        userid,
        secret: "string",
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data?.response || [],
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed to fetch menu items",
          data: [],
        };
      }
    } catch (e) {
      console.error("getMerchantMenuItems error:", e);
      return {
        success: false,
        message: "Error fetching menu items",
        data: [],
      };
    }
  };
  const addFreeItems = async ({ userid, minAmount, singleUse, item }) => {
    try {
      const { data } = await api.post("/Order/v1.0/addFreeItems", {
        userid,
        minAmount,
        singleUse,
        item,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Saved",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("addFreeItems error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const getMerchantLoyaltyPointsConfig = async (userid) => {
    try {
      const { data } = await api.post(
        "/Order/v1.0/getMerchantLoyaltyPointsConfig",
        { userid },
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data?.data || [],
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed to fetch loyalty config",
          data: [],
        };
      }
    } catch (e) {
      console.error("getMerchantLoyaltyPointsConfig error:", e);
      return {
        success: false,
        message: "Error fetching loyalty config",
        data: [],
      };
    }
  };
  const updateMerchantLoyaltyPointsConfig = async ({
    userid,
    points,
    value,
  }) => {
    try {
      const { data } = await api.post(
        "/Order/v1.0/updateMerchantLoyaltyPointsConfig",
        { userid, points, value },
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Saved",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("updateMerchantLoyaltyPointsConfig error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };

  const getOrderChargesConfig = async (userid) => {
    try {
      const { data } = await api.post("/Order/v1.0/getOrderChargesConfig", {
        userid,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data?.response || [],
        };
      } else {
        return {
          success: false,
          message:
            data?.responseMessage || "Failed to fetch order charges config",
          data: [],
        };
      }
    } catch (e) {
      console.error("getOrderChargesConfig error:", e);
      return {
        success: false,
        message: "Error fetching order charges config",
        data: [],
      };
    }
  };
  const insertOrderChargesConfig = async ({
    userid,
    adminId,
    fromAmount,
    toAmount,
    charges,
    chargesType,
    chargesOn,
    orderType,
  }) => {
    try {
      const { data } = await api.post("/Order/v1.0/insertOrderChargesConfig", {
        userid,
        adminId,
        fromAmount,
        toAmount,
        charges,
        chargesType,
        chargesOn,
        orderType,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Saved",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("insertOrderChargesConfig error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };

  const getRestaurantTiming = async (userid) => {
    try {
      const { data } = await api.post("/Order/v1.0/getRestaurantTiming", {
        userid,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data?.data || null,
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed to fetch restaurant timing",
          data: null,
        };
      }
    } catch (e) {
      console.error("getRestaurantTiming error:", e);
      return {
        success: false,
        message: "Error fetching restaurant timing",
        data: null,
      };
    }
  };
  const updateRestaurantTiming = async ({
    userid,
    opening,
    closing,
    weekOff,
  }) => {
    try {
      const { data } = await api.post("/Order/v1.0/updateRestaurantTiming", {
        userid,
        opening,
        closing,
        weekOff,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Saved",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("updateRestaurantTiming error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const getReceiptData = async (userid) => {
    try {
      const { data } = await api.post("/Order/getReceiptData", { userid });

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data?.response || [],
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed to fetch receipt data",
          data: [],
        };
      }
    } catch (e) {
      console.error("getReceiptData error:", e);
      return {
        success: false,
        message: "Error fetching receipt data",
        data: [],
      };
    }
  };
  const updatePrintSettings = async ({
    userid,
    normalFontSize,
    largeFontSize,
    pageWidth,
    anlookActive,
    autoPrint,
  }) => {
    try {
      const { data } = await api.post("/Order/v1.0/updatePrintSettings", {
        userid,
        printConfig: [
          { key: "normalFontSize", value: normalFontSize },
          { key: "largeFontSize", value: largeFontSize },
          { key: "pageWidth", value: pageWidth },
          { key: "anlookActive", value: anlookActive ? "1" : "0" },
          { key: "autoPrint", value: autoPrint ? "1" : "0" },
        ],
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Saved",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("updatePrintSettings error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  const getOrderTypeOptions = async (userid) => {
    try {
      const { data } = await api.post("/Order/v1.0/getOrderTypeOptions", {
        userid,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data?.response || [],
        };
      } else {
        return {
          success: false,
          message:
            data?.responseMessage || "Failed to fetch order type options",
          data: [],
        };
      }
    } catch (e) {
      console.error("getOrderTypeOptions error:", e);
      return {
        success: false,
        message: "Error fetching order type options",
        data: [],
      };
    }
  };
  const updateOrderTypeOptions = async ({ userid, orderType, status }) => {
    try {
      const { data } = await api.post("/Order/v1.0/updateOrderTypeOptions", {
        userid,
        orderType,
        status,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Updated",
        };
      } else {
        return {
          success: false,
          message: data?.responseMessage || "Failed",
        };
      }
    } catch (e) {
      console.error("updateOrderTypeOptions error:", e);
      return {
        success: false,
        message: "Failed",
      };
    }
  };
  //  Manage restaurant timing api's are added at the end as they are directly related to order settings and to avoid merge conflicts with ongoing work in order timing section

  const normalizeRestaurantTimings = (data) => {
    const raw = data?.result || data?.response || data?.data || [];

    if (!Array.isArray(raw)) return [];

    return raw.filter((item) => item?.status === "1" || item?.status === 1);
  };

  const getRestaurantTimingsV2 = async (restaurantId) => {
    try {
      const { data } = await api.post(
        "/Admin/manageRestuarantTimings",
        {
          restaurantId: String(restaurantId),
          requests: [],
        },
        {
          headers: {
            flag: "GET",
            "Content-Type": "application/json",
          },
        },
      );

      const isSuccess =
        data?.status === "1" ||
        data?.status === 1 ||
        data?.responseCode === "00";

      if (isSuccess) {
        return {
          success: true,
          data: normalizeRestaurantTimings(data),
          message: data?.msg || data?.responseMessage || "Timings fetched",
        };
      }

      return {
        success: false,
        data: [],
        message:
          data?.msg ||
          data?.responseMessage ||
          "Failed to fetch restaurant timings",
      };
    } catch (e) {
      console.error("getRestaurantTimingsV2 error:", e);
      return {
        success: false,
        data: [],
        message: "Error fetching restaurant timings",
      };
    }
  };

  const saveRestaurantTimingsV2 = async ({ restaurantId, requests }) => {
    try {
      const { data } = await api.post(
        "/Admin/manageRestuarantTimings",
        {
          restaurantId: String(restaurantId),
          requests,
        },
        {
          headers: {
            flag: "INSERT",
            "Content-Type": "application/json",
          },
        },
      );

      const isSuccess =
        data?.status === "1" ||
        data?.status === 1 ||
        data?.responseCode === "00";

      if (isSuccess) {
        return {
          success: true,
          message: data?.msg || data?.responseMessage || "Timings saved",
          data: data?.result || data?.response || [],
        };
      }

      return {
        success: false,
        message:
          data?.msg ||
          data?.responseMessage ||
          "Failed to save restaurant timings",
      };
    } catch (e) {
      console.error("saveRestaurantTimingsV2 error:", e);
      return {
        success: false,
        message: "Failed to save restaurant timings",
      };
    }
  };
  return {
    clients,
    orders,
    loading,
    busy,
    getCatalogBannersAdmin,
    addCatalogBanner,
    deleteCatalogBanner,
    getDeliveryProvider,
    updateDeliveryProvider,
    getSettlementAccountList,
    addSettlementAccount,
    getOrganizationDetails,
    editOrganizationInfo,
    getMerchantCouponCodes,
    createMerchantCouponCode,
    disableMerchantCouponCode,
    getDeliveryChargesAndDiscount,
    updateDeliveryCharges,
    deleteDeliveryCharges,
    updateDiscount,
    deleteDiscount,
    getMerchantMenuItems,
    addFreeItems,
    getMerchantLoyaltyPointsConfig,
    updateMerchantLoyaltyPointsConfig,
    getOrderChargesConfig,
    insertOrderChargesConfig,
    getRestaurantTiming,
    updateRestaurantTiming,
    getReceiptData,
    updatePrintSettings,
    getOrderTypeOptions,
    updateOrderTypeOptions,
    getRestaurantTimingsV2,
    saveRestaurantTimingsV2,
  };
}
